import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import heroVideo from "../assets/videos/Product.mp4"; // Adjust the path as necessary

const Stat = ({ title, subtitle }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const statRef = useRef(null);

  // Extract the numeric value from the title
  const getNumericValue = (str) => {
    const match = str.match(/(\d+)/);
    return match ? parseInt(match[1]) : 0;
  };

  // Extract the suffix from the title (e.g., "+", "%", etc.)
  const getSuffix = (str) => {
    const match = str.match(/[^\d]+$/);
    return match ? match[0] : '';
  };

  const numericValue = getNumericValue(title);
  const suffix = getSuffix(title);

  useEffect(() => {
    const currentStatRef = statRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (currentStatRef) {
      observer.observe(currentStatRef);
    }

    return () => {
      if (currentStatRef) {
        observer.unobserve(currentStatRef);
      }
    };
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    let startTimestamp = null;
    const duration = 2000; // Animation duration in ms

    const animate = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuad = (t) => t * (2 - t);
      
      setCount(Math.floor(easeOutQuad(progress) * numericValue));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, numericValue]);

  // Handle special cases like "24/7"
  if (title === "24/7") {
    return (
      <div ref={statRef} className="flex flex-col items-center text-center p-3 md:p-4 backdrop-blur-md bg-black bg-opacity-30 rounded-xl border border-gray-700 shadow-lg">
        <span className="text-lg md:text-3xl font-bold text-red-500">{title}</span>
        <span className="text-xs md:text-base text-gray-300 mt-0.5 md:mt-1">
          {subtitle}
        </span>
      </div>
    );
  }

  return (
    <div ref={statRef} className="flex flex-col items-center text-center p-3 md:p-4 backdrop-blur-md bg-black bg-opacity-30 rounded-xl border border-gray-700 shadow-lg">
      <span className="text-lg md:text-3xl font-bold text-red-500">
        {count}{suffix}
      </span>
      <span className="text-xs md:text-base text-gray-300 mt-0.5 md:mt-1">
        {subtitle}
      </span>
    </div>
  );
};

const Hero = () => {
  return (
    <div className="relative min-h-screen bg-black text-white overflow-hidden">
      {/* Background Video */}
      <div className="absolute inset-0 z-0">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover"
        >
          <source src={heroVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
      </div>

      {/* Hero Content */}
      <div className="relative z-10 flex flex-col justify-center items-center text-center px-4 md:px-12 min-h-screen">
        <h1 className="text-2xl md:text-6xl font-extrabold bg-gradient-to-r from-red-400 to-red-600 bg-clip-text text-transparent mb-3 md:mb-6 animate-fade-in leading-tight max-w-[90%] md:max-w-none">
          Craft Your Vision in 3D
        </h1>
        <p className="text-sm md:text-xl text-white max-w-[90%] md:max-w-2xl mb-6 md:mb-8 animate-slide-up leading-relaxed">
          Cutting-edge 3D printing for prototypes, parts, and products. Turn
          your concept into reality with speed, precision, and flair.
        </p>

        {/* Buttons */}
        <div className="flex flex-col md:flex-row gap-3 md:gap-4 w-full max-w-[90%] md:max-w-none justify-center animate-slide-up">
          <Link
            to="/services"
            className="px-4 md:px-6 py-2.5 md:py-3 bg-red-600 hover:bg-red-700 transition text-white font-semibold rounded-full shadow-lg w-full md:w-auto text-sm md:text-base"
          >
            Our Services
          </Link>
          <Link
            to="/contact"
            className="px-4 md:px-6 py-2.5 md:py-3 border border-red-500 text-red-400 hover:bg-red-500 hover:text-white transition font-semibold rounded-full w-full md:w-auto text-sm md:text-base"
          >
          Contact Us 
          </Link>
        </div>

        {/* Stats Section */}
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-x-10 md:gap-y-6 w-full max-w-screen-lg mx-auto px-2 animate-fade-in">
          <Stat title="1500+" subtitle="Projects Completed" />
          <Stat title="70+" subtitle="Trusted Clients" />
          <Stat title="98%" subtitle="Repeat Orders" />
          <Stat title="24/7" subtitle="Support" />
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-6 md:bottom-10 left-1/2 transform -translate-x-1/2 flex flex-col items-center animate-fade-in">
        <span className="text-xs md:text-sm text-gray-300 mb-1.5 md:mb-2">
          Scroll to explore
        </span>
        <div className="w-4 h-6 md:w-6 md:h-10 border-2 border-gray-400 rounded-full flex justify-center items-start overflow-hidden">
          <div className="w-1 h-1 md:w-1.5 md:h-1.5 bg-cyan-400 rounded-full mt-1 md:mt-2 animate-bounce-smooth"></div>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes bounceSmooth {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(6px); }
        }
        .animate-fade-in {
          animation: fadeIn 1s ease-out forwards;
        }
        .animate-slide-up {
          animation: slideUp 1s ease-out forwards;
        }
        .animate-bounce-smooth {
          animation: bounceSmooth 1.2s infinite;
        }
      `}</style>
    </div>
  );
};

export default Hero;